import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { db, reportDbError } from "./db";
import { SyncEngine } from "./sync";
import { uuidv7 } from "../../shared/src/ids";
import type {
  CommandType,
  CommandPayload,
  CommandEnvelope,
} from "../../shared/src/commands";
import type { EventEnvelope, TypedEvent } from "../../shared/src/events";
import type { QueryType } from "../../shared/src/protocol";
import { applyClientProjection } from "./projections";
import { events } from "../../shared/src/schema";
import { pinCase } from "./retention";
import { useI18n } from "./i18n";

// ── Lightweight version store (outside React) ──────────────────────
// A simple external store: version increments on each event, subscribers
// are notified. Components subscribe via useSyncExternalStore which only
// triggers a re-render when the snapshot value (version number) actually
// changes.  This avoids pumping a React state through the entire tree.
let _version = 0;
const _versionListeners = new Set<() => void>();
function bumpVersion() {
  _version++;
  for (const fn of _versionListeners) fn();
}
export function invalidateQueries() { bumpVersion(); }
function subscribeVersion(cb: () => void) {
  _versionListeners.add(cb);
  return () => {
    _versionListeners.delete(cb);
  };
}
function getVersion() {
  return _version;
}

// ── List-invalidation version store ──────────────────────────────────
// Separate counter for server-side list queries (cases/patients).
// Bumped when the server sends an `invalidate` message.
let _listVersion = 0;
const _listVersionListeners = new Set<() => void>();
function bumpListVersion() {
  _listVersion++;
  for (const fn of _listVersionListeners) fn();
}
function subscribeListVersion(cb: () => void) {
  _listVersionListeners.add(cb);
  return () => {
    _listVersionListeners.delete(cb);
  };
}
function getListVersion() {
  return _listVersion;
}

// ── Live broadcast-message listeners ─────────────────────────────────
// Fires when a message.send event reaches this client via WS. The server
// only forwards to sessions whose auth matches the audience, so any event
// surfaced here is one the user is meant to see.
type MessageArrival = {
  id: string;
  senderId: string;
  body: string;
  audienceType: string;
  audienceHospitalId?: string;
  audienceUnitIds?: string[];
  createdAt: number;
};
const _messageArrivalListeners = new Set<(m: MessageArrival) => void>();
function notifyMessageArrival(m: MessageArrival) {
  for (const fn of _messageArrivalListeners) {
    try {
      fn(m);
    } catch (e) {
      console.warn("[Store] message listener threw", e);
    }
  }
}
export function subscribeToMessageArrivals(
  cb: (m: MessageArrival) => void,
): () => void {
  _messageArrivalListeners.add(cb);
  return () => {
    _messageArrivalListeners.delete(cb);
  };
}

interface StoreContextValue {
  dispatch: (
    type: CommandType,
    payload: CommandPayload,
  ) => Promise<{ ack: Promise<void> }>;
  bulkDispatch: (
    cmds: { type: CommandType; payload: CommandPayload }[],
  ) => Promise<void>;
  rpc: <T = unknown>(query: QueryType) => Promise<T>;
  openCase: (caseId: string) => Promise<boolean>;
  syncEntity: (entityId: string) => Promise<void>;
  online: boolean;
  connecting: boolean;
  syncing: boolean; // True during initial sync
  pendingCount: number;
}

const StoreContext = createContext<StoreContextValue | null>(null);

interface StoreProviderProps {
  children: ReactNode;
  sessionId: string;
  userId: string;
  wsUrl: string;
  onAuthFailure?: () => void;
}

export function StoreProvider({
  children,
  sessionId,
  userId,
  wsUrl,
  onAuthFailure,
}: StoreProviderProps) {
  const { t } = useI18n();
  const [online, setOnline] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [versionMismatch, setVersionMismatch] = useState(false);
  const [rejectionCount, setRejectionCount] = useState(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const rejectionCountRef = useRef(0);
  const trackRejection = useCallback(() => {
    rejectionCountRef.current++;
    setRejectionCount(rejectionCountRef.current);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      rejectionCountRef.current = 0;
      setRejectionCount(0);
    }, 1000);
  }, []);
  const engineRef = useRef<SyncEngine | null>(null);
  // Hold a live reference to the latest translator so the engine callback
  // (created once) always picks the current language for native notifications.
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const engine = new SyncEngine(
      wsUrl,
      sessionId,
      async (event: EventEnvelope) => {
        await applyClientProjection(event);
        if (event.type === "message.send" && event.userId !== userId) {
          // Skip echoes of our own send — only notify on inbound messages.
          const e = event as TypedEvent<"message.send">;
          const { body, audienceType, audienceHospitalId, audienceUnitIds } =
            e.payload;
          notifyMessageArrival({
            id: e.entityId,
            senderId: e.userId,
            body,
            audienceType,
            audienceHospitalId,
            audienceUnitIds,
            createdAt: e.clientTimestamp,
          });
          // Native push: only when the page isn't visible. The Android shell
          // additionally suppresses if the activity is foregrounded, so the
          // in-app toast handles the visible case.
          if (
            typeof document !== "undefined" &&
            document.visibilityState !== "visible" &&
            window.DataLab?.notify
          ) {
            try {
              window.DataLab.notify(
                tRef.current("messages.newBroadcast"),
                body,
                "/messages",
              );
            } catch (err) {
              console.warn("[Store] DataLab.notify failed", err);
            }
          }
        }
      },
      onAuthFailure,
      () => setVersionMismatch(true),
    );
    engineRef.current = engine;

    const unsub = engine.onEvent(() => {
      setOnline(engine.online);
      setConnecting(engine.connecting);
      setSyncing(engine.syncing);
      setPendingCount(engine.pendingCount);
      bumpVersion();
    });

    const unsubInvalidate = engine.onInvalidate(() => {
      bumpListVersion();
      bumpVersion();
    });

    engine.start();

    // Re-query local data when browser tab regains focus (WS may have
    // reconnected and synced new events while the tab was hidden).
    const onVisible = () => {
      if (document.visibilityState === "visible") bumpVersion();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      unsub();
      unsubInvalidate();
      engine.stop();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [wsUrl, sessionId, onAuthFailure]);

  const dispatch = useCallback(
    async (
      type: CommandType,
      payload: CommandPayload,
    ): Promise<{ ack: Promise<void> }> => {
      const now = Date.now();
      const cmd: CommandEnvelope = {
        id: uuidv7(),
        type,
        payload,
        userId,
        sessionId,
        clientTimestamp: now,
      };

      // Extract entityId from payload (all commands have a 'uuid' field)
      const entityId = (payload as { uuid: string }).uuid;

      try {
        // Optimistic: insert event into local DB immediately
        await db
          .insert(events)
          .values({
            id: cmd.id,
            type: cmd.type,
            entityId,
            payload: cmd.payload as unknown as object,
            userId: cmd.userId,
            sessionId: cmd.sessionId,
            clientTimestamp: now,
            serverTimestamp: 0, // Will be updated when server confirms
          })
          .onConflictDoNothing();

        // Apply projection (will now find the event in DB)
        const optimisticEvent = {
          ...cmd,
          entityId,
          serverTimestamp: 0,
        } as EventEnvelope;
        await applyClientProjection(optimisticEvent);
        bumpVersion();
      } catch (err) {
        if (err instanceof Error && /sqlite/i.test(err.message))
          reportDbError(err);
        throw err;
      }

      // Pin locally created cases so they survive pruning until synced
      if (type === "case.create") {
        await pinCase(entityId);
      }

      // Enqueue and send — returns a promise that resolves on server ack
      // Wrapped in an object to prevent async auto-flattening
      const ackPromise = engineRef.current?.dispatch(cmd) ?? Promise.resolve();
      ackPromise.catch(() => {
        trackRejection();
      });
      setPendingCount(engineRef.current?.pendingCount ?? 0);
      return { ack: ackPromise };
    },
    [userId, sessionId],
  );

  const bulkDispatch = useCallback(
    async (items: { type: CommandType; payload: CommandPayload }[]) => {
      const now = Date.now();
      const cmds = items.map(({ type, payload }) => ({
        id: uuidv7(),
        type,
        payload,
        userId,
        sessionId,
        clientTimestamp: now,
      }));
      await engineRef.current?.bulkDispatch(cmds);
    },
    [userId, sessionId],
  );

  const rpc = useCallback(<T = unknown,>(query: QueryType): Promise<T> => {
    if (!engineRef.current) {
      return Promise.reject(new Error("Not initialized"));
    }
    return engineRef.current.query<T>(query);
  }, []);

  const openCase = useCallback(async (caseId: string): Promise<boolean> => {
    return engineRef.current?.openCase(caseId) ?? true;
  }, []);

  const syncEntity = useCallback(async (entityId: string): Promise<void> => {
    await engineRef.current?.syncEntity(entityId);
  }, []);

  return (
    <StoreContext.Provider
      value={{
        dispatch,
        bulkDispatch,
        rpc,
        openCase,
        syncEntity,
        online,
        connecting,
        syncing,
        pendingCount,
      }}
    >
      {versionMismatch && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              colorScheme: "light dark",
              background: "Canvas",
              color: "CanvasText",
              padding: "2rem",
              borderRadius: "8px",
              textAlign: "center",
              maxWidth: "400px",
            }}
          >
            <h2 style={{ margin: "0 0 1rem" }}>{t("sync.updateRequired")}</h2>
            <p style={{ margin: "0 0 1.5rem", opacity: 0.6 }}>
              {t("sync.updateRequiredDesc")}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: "#2563eb",
                color: "white",
                border: "none",
                padding: "0.75rem 2rem",
                borderRadius: "6px",
                fontSize: "1rem",
                cursor: "pointer",
              }}
            >
              {t("sync.reload")}
            </button>
          </div>
        </div>
      )}
      {rejectionCount > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#dc2626",
            color: "white",
            padding: "0.75rem 1.5rem",
            borderRadius: "8px",
            fontSize: "0.875rem",
            zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            cursor: "pointer",
            maxWidth: "90vw",
          }}
          onClick={() => {
            rejectionCountRef.current = 0;
            setRejectionCount(0);
          }}
        >
          {t("sync.operationsRejected", { count: String(rejectionCount) })}
        </div>
      )}
      <MessageArrivalToast />
      {children}
    </StoreContext.Provider>
  );
}

function MessageArrivalToast() {
  const { t } = useI18n();
  const [latest, setLatest] = useState<MessageArrival | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const unsub = subscribeToMessageArrivals((m) => {
      setLatest(m);
      clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setLatest(null), 8000);
    });
    return () => {
      unsub();
      clearTimeout(dismissTimer.current);
    };
  }, []);

  if (!latest) return null;
  const preview = latest.body.length > 140 ? latest.body.slice(0, 137) + "…" : latest.body;

  return (
    <a
      href="/messages"
      style={{
        position: "fixed",
        top: "1rem",
        right: "1rem",
        zIndex: 9999,
        maxWidth: "min(28rem, 90vw)",
        padding: "0.75rem 1rem",
        borderRadius: "8px",
        background: "#1f2937",
        color: "white",
        boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
        textDecoration: "none",
        cursor: "pointer",
        display: "block",
      }}
      onClick={(e) => {
        // Allow normal navigation; just dismiss the toast immediately.
        clearTimeout(dismissTimer.current);
        setLatest(null);
        // Don't preventDefault — let the browser navigate to /messages
        e.stopPropagation();
      }}
    >
      <div style={{ fontSize: "0.7rem", opacity: 0.7, marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {t("messages.newBroadcast")}
      </div>
      <div style={{ fontSize: "0.9rem", lineHeight: 1.35, whiteSpace: "pre-wrap" }}>{preview}</div>
    </a>
  );
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}

export function useCommand() {
  const { dispatch } = useStore();
  return dispatch;
}

export function useRpc() {
  const { rpc } = useStore();
  return rpc;
}

export function useOnlineStatus() {
  const { online } = useStore();
  return online;
}

export function useSyncStatus() {
  const { online, connecting, syncing, pendingCount } = useStore();
  return { online, connecting, syncing, pendingCount };
}

/**
 * Re-run a query whenever the store version changes.
 *
 * Key behaviour to avoid DOM flashing:
 *  - `loading` is only `true` on the very first fetch (data === null).
 *  - Subsequent re-fetches triggered by new events happen silently — the
 *    previous data stays on screen while the new result is computed, then
 *    React diffs the result and only touches the DOM if something changed.
 *  - We use `useSyncExternalStore` for the version counter so that the
 *    re-query trigger doesn't propagate a state update through the whole
 *    provider tree.
 */
export function useListVersion(): number {
  return useSyncExternalStore(subscribeListVersion, getListVersion);
}

// Query result cache — keyed by JSON-serialised deps.  Survives unmount so
// navigating back shows cached data instantly while the query re-runs silently.
const _queryCache = new Map<string, unknown>();

export function useQuery<T>(
  queryFn: () => Promise<T>,
  deps: unknown[] = [],
): { data: T | null; loading: boolean } {
  const version = useSyncExternalStore(subscribeVersion, getVersion);

  // Derive a stable key from deps. Version bumps don't change this,
  // so queries are never starved by rapid sync events during reconnect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const depsKey = useMemo(() => JSON.stringify(deps), deps);
  const depsKeyRef = useRef(depsKey);
  depsKeyRef.current = depsKey;

  const [data, setData] = useState<T | null>(() => {
    const cached = _queryCache.get(depsKey);
    return cached !== undefined ? (cached as T) : null;
  });
  const [loading, setLoading] = useState(() => !_queryCache.has(depsKey));

  // When deps change, synchronously reset data/loading from cache (or to null/true).
  // This runs during render so the stale result from the old deps is never painted.
  const prevDepsKeyRef = useRef(depsKey);
  if (prevDepsKeyRef.current !== depsKey) {
    prevDepsKeyRef.current = depsKey;
    const cached = _queryCache.get(depsKey);
    if (cached !== undefined) {
      setData(cached as T);
      setLoading(false);
    } else {
      setData(null);
      setLoading(true);
    }
  }

  useEffect(() => {
    const key = depsKey;
    queryFn()
      .then((result) => {
        if (key === depsKeyRef.current) {
          setData(result);
          setLoading(false);
          _queryCache.set(key, result);
          // Simple eviction: drop oldest entry when cache is too large
          if (_queryCache.size > 64) {
            const first = _queryCache.keys().next().value;
            if (first !== undefined) _queryCache.delete(first);
          }
        }
      })
      .catch((err) => {
        console.error("[useQuery] error:", err);
        if (err instanceof Error && /sqlite/i.test(err.message))
          reportDbError(err);
        if (key === depsKeyRef.current) setLoading(false);
      });
  }, [version, depsKey]);

  return { data, loading };
}
